import { Box, Text, VStack, Flex, Icon, Checkbox, HStack } from '@chakra-ui/react';
import AuthWrapper from '../../../layouts/AuthWrapper';
import { useForm, Controller } from 'react-hook-form';
import CustomInput from '../../../components/common/Input';
import emailIcon from '../../../assets/svg/mail.svg?react';
import lockIcon from '../../../assets/svg/lock.svg?react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { NAVIGATION_ROUTES } from '../../../routes/constants';
import { useNavigate } from 'react-router-dom';
import CustomButton from '../../../components/common/Button';
import { useLoginMutation } from '@/services/auth/auth';
import { AxiosError } from 'axios';
import useCustomToast from '@/components/common/Toast';

export interface ILoginData {
  email: string;
  password: string;
  remember_me: boolean;
}

const validationSchema = Yup.object().shape({
  email: Yup.string().email('Please enter a valid email address.').required('Email is required.'),
  password: Yup.string().required('Password is required.'),
  remember_me: Yup.boolean().required(),
});

const SignIn = () => {
  const navigate = useNavigate();
  const { mutateAsync: loginUser, isLoading: userLoginLoading } = useLoginMutation();
  const { showToast } = useCustomToast();

  const {
    handleSubmit,
    control,
    setError,
    formState: { errors, isSubmitted },
    clearErrors,
  } = useForm<ILoginData>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      email: '',
      password: '',
      remember_me: false,
    },
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    shouldFocusError: true,
  });

  const onSubmit = async (data: ILoginData) => {
    try {
      await loginUser(data);
      showToast('success', 'Login successful.');
      navigate(NAVIGATION_ROUTES.HOME);
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        const responseData = error.response?.data;

        if (
          responseData &&
          typeof responseData === 'object' &&
          'detail' in responseData &&
          responseData.detail === 'OTP is not verified' &&
          responseData.is_otp_verified === false
        ) {
          navigate(NAVIGATION_ROUTES.EMAILVERIFICATION, {
            state: {
              email: data.email, // pass the email user just entered
              fromSignInOtpNotVerified: true, // this flag triggers auto-resend
            },
          });
          return;
        }

        // Existing behavior: if there's an 'error' field (e.g., invalid credentials), show it on password field
        if (responseData?.error) {
          setError('password', {
            type: 'manual',
            message: responseData.error,
          });
        } else {
          // For other cases with 'detail' (or any other error), show toast
          showToast('error', responseData?.detail || 'An error occurred during login.');
        }
      } else {
        // Non-Axios errors (unlikely)
        showToast('error', 'An unexpected error occurred.');
      }
    }
  };

  return (
    <AuthWrapper>
      <Box w="full" maxW="lg" p={8} bg="white" fontFamily={'primary'}>
        <VStack spacing={8} align="stretch">
          <Flex direction={'column'} gap={1}>
            <Text color={'text.primary'} fontSize="4xl" fontWeight={'semibold'}>
              Welcome Back
            </Text>
            <Text color={'text.secondary'} fontSize={'sm'} fontWeight={'normal'}>
              Welcome back! Please enter your details.
            </Text>
          </Flex>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Flex direction={'column'} gap={6}>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <CustomInput
                    {...field}
                    label="Email"
                    placeholder="Enter your email"
                    leftIcon={
                      <Icon
                        as={emailIcon}
                        color={errors.email ? 'status.error' : 'text.secondary'}
                      />
                    }
                    error={isSubmitted ? errors.email : undefined}
                    onChange={(e) => {
                      field.onChange(e);
                      if (errors.email) clearErrors('email');
                    }}
                  />
                )}
              />
              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <CustomInput
                    {...field}
                    label="Password"
                    type="password"
                    placeholder="Enter your password"
                    leftIcon={
                      <Icon
                        as={lockIcon}
                        color={errors.password ? 'status.error' : 'text.secondary'}
                      />
                    }
                    error={isSubmitted ? errors.password : undefined}
                    onChange={(e) => {
                      field.onChange(e);
                      if (errors.password) clearErrors('password');
                    }}
                  />
                )}
              />

              <Flex justifyContent="space-between" alignItems="center">
                <Controller
                  name="remember_me"
                  control={control}
                  defaultValue={false}
                  render={({ field }) => {
                    const { onChange, value, ref } = field;
                    return (
                      <Checkbox
                        isChecked={value}
                        onChange={(e) => onChange(e.target.checked)}
                        ref={ref}
                        size="md"
                        borderColor="border.default"
                        colorScheme="brand.primary"
                        display="flex"
                        alignItems="center"
                      >
                        <Text fontSize="sm" color="text.secondary" fontFamily="primary" mt={1}>
                          Remember me
                        </Text>
                      </Checkbox>
                    );
                  }}
                />
                <Text
                  color="brand.primary.DEFAULT"
                  fontFamily={'primary'}
                  fontSize={'sm'}
                  fontWeight={'semibold'}
                  cursor={'pointer'}
                  onClick={() => {
                    navigate(NAVIGATION_ROUTES.PASSWORD.FORGOT);
                  }}
                  _hover={{
                    color: 'brand.primary.700',
                    textDecoration: 'underline',
                  }}
                >
                  Forgot password?
                </Text>
              </Flex>
              <CustomButton
                width="100%"
                fontSize="md"
                fontWeight="semibold"
                type="submit"
                isLoading={userLoginLoading}
                fontFamily={'primary'}
              >
                Sign in
              </CustomButton>
            </Flex>
          </form>
          <HStack spacing={1} justifyContent="center" fontFamily={'primary'}>
            <Text fontSize="sm">Do not have an account?</Text>

            <Text
              color="brand.primary.DEFAULT"
              fontSize="sm"
              cursor="pointer"
              fontWeight={'semibold'}
              onClick={() => navigate(NAVIGATION_ROUTES.SIGNUP)}
              _hover={{
                color: 'brand.primary.700',
                textDecoration: 'underline',
              }}
            >
              Sign Up
            </Text>
          </HStack>
        </VStack>
      </Box>{' '}
    </AuthWrapper>
  );
};

export default SignIn;
